import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpenIcon, GraduationCapIcon, LanguagesIcon, SearchIcon, Trash2Icon } from "lucide-react";
import { toast } from "sonner";
import { useLearner } from "../../contexts/LearnerContext";
import { useWords } from "../../contexts/WordsContext";
import { PageHeader } from "../../components/app/PageHeader";
import { Segmented } from "../../components/ui/Segmented";
import { Tag } from "../../components/ui/Tag";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { SpeakButton } from "../../components/games/SpeakButton";
import type { WordSource, WordStatus } from "../../types/words";
import { courseTitle, getLanguage } from "../../utils/language";
import { practicePath, wordsPath } from "../../utils/routes";
import { useRouteCourse } from "../../hooks/useRouteCourse";
import { useNavigate } from "react-router-dom";
import { spring } from "../../utils/motion";
const sourceMeta: Record<WordSource, {
  label: string;
  icon: typeof LanguagesIcon;
}> = {
  translator: {
    label: "Translator",
    icon: LanguagesIcon
  },
  book: {
    label: "Book",
    icon: BookOpenIcon
  },
  lesson: {
    label: "Lesson",
    icon: GraduationCapIcon
  }
};
const statusTone: Record<WordStatus, "accent" | "tint" | "good"> = {
  new: "accent",
  practising: "tint",
  mastered: "good"
};
export function MyWords() {
  const {
    courses
  } = useLearner();
  const {
    words,
    removeWord,
    saveWord,
    dueWords
  } = useWords();
  const navigate = useNavigate();
  const routeCourse = useRouteCourse();
  const courseId = routeCourse?.id ?? "";
  // Switching the course pair updates the URL: /app/words/:from/:to
  const setCourseId = (id: string) => {
    const next = courses.find(c => c.id === id);
    if (next) navigate(wordsPath(next));
  };
  const [status, setStatus] = useState<"all" | WordStatus>("all");
  const [query, setQuery] = useState("");
  const course = courses.find(c => c.id === courseId);
  const inCourse = words.filter(w => w.courseId === courseId);
  const count = (s: WordStatus) => inCourse.filter(w => w.status === s).length;
  const due = dueWords(courseId);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return inCourse.filter(w => (status === "all" || w.status === status) && (!q || w.term.toLowerCase().includes(q) || w.translation.toLowerCase().includes(q)));
  }, [inCourse, status, query]);
  const remove = (id: string) => {
    const w = words.find(x => x.id === id);
    if (!w) return;
    removeWord(id);
    toast(`Removed “${w.term}”`, {
      action: {
        label: "Undo",
        onClick: () => saveWord({
          term: w.term,
          translation: w.translation,
          courseId: w.courseId,
          source: w.source
        })
      }
    });
  };
  if (courses.length === 0) {
    return <div className="mx-auto max-w-5xl px-4 py-6 md:px-8 md:py-10" data-id="element-1337" data-mp="7x3kl9kv0k3i">
        <PageHeader title="My Words" description="Add a course first — then everything you save lands here." data-id="element-1338" data-mp="cvesherlgkj3" />
        <div className="mt-6" data-id="element-1339" data-mp="1xdz5j61991vr">
          <BrutalButton to="/app/courses" data-id="element-1340" data-mp="1xd79xv7nr45h">Add a course</BrutalButton>
        </div>
      </div>;
  }
  return <div className="mx-auto max-w-5xl px-4 py-6 md:px-8 md:py-10" data-id="element-1341" data-mp="1sbrq26h7v3oo">
      <PageHeader title="My Words" description="Everything you've saved from the translator, the book and lessons. Saved words also appear inside regular lessons, tagged." actions={<BrutalButton to={course ? practicePath(course) : "/app"} disabled={due.length === 0} data-id="element-1343" data-mp="2g1uco72d3hfw">
            Practise {Math.min(due.length, 10)} due
          </BrutalButton>} data-id="element-1342" data-mp="273ioscsvqxr5" />

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-1344" data-mp="2q0a1g3dx2em8">
        <div className="no-scrollbar -mx-4 overflow-x-auto px-4 lg:mx-0 lg:px-0" data-id="element-1345" data-mp="2l3tgfi20kgdd">
          <Segmented id="words-course" label="Course" value={courseId} onChange={setCourseId} options={courses.map(c => ({
          value: c.id,
          label: `${getLanguage(c.target).name} · ${c.base.toUpperCase()}`
        }))} data-id="element-1346" data-mp="vomzzy0irfwv" />
        </div>
        <div className="no-scrollbar -mx-4 overflow-x-auto px-4 lg:mx-0 lg:px-0" data-id="element-1347" data-mp="x3y1wkv8uom0">
          <Segmented id="words-status" label="Status" value={status} onChange={setStatus} options={[{
          value: "all",
          label: `All ${inCourse.length}`
        }, {
          value: "new",
          label: `New ${count("new")}`
        }, {
          value: "practising",
          label: `Practising ${count("practising")}`
        }, {
          value: "mastered",
          label: `Mastered ${count("mastered")}`
        }]} data-id="element-1348" data-mp="1243qfs67vuzv" />
        </div>
      </div>

      <label className="mt-4 flex items-center gap-2.5 rounded-xl border-3 border-line bg-surface px-3.5" data-id="element-1349" data-mp="o3sysmx32cdn">
        <SearchIcon className="h-4 w-4 text-ink-2" aria-hidden="true" data-id="element-1350" data-mp="3mukumy9xciae" />
        <span className="sr-only" data-id="element-1351" data-mp="3rt5yr8sarm44">Search saved words</span>
        <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={`Search ${course ? courseTitle(course) : ""} words`} className="w-full bg-transparent py-3 text-[15px] text-ink placeholder:text-ink-2/70 outline-none" data-id="element-1352" data-mp="3rrl3ti22m15q" />
      </label>

      <div className="mt-5 overflow-hidden rounded-card border-3 border-line bg-surface shadow-brutal" data-id="element-1353" data-mp="1zzg0tqfjelem">
        <div className="hidden grid-cols-[1.4fr_1.2fr_120px_130px_110px_48px] gap-4 border-b-3 border-line bg-sunken px-4 py-2.5 text-xs font-bold text-ink-2 md:grid" data-id="element-1354" data-mp="1v0mlrtklqmcc">
          <span data-id="element-1355" data-mp="1l6w2r478v441">Word</span>
          <span data-id="element-1356" data-mp="1l6m9n7qwkgu4">Meaning</span>
          <span data-id="element-1357" data-mp="1h6c4fxaxpugb">From</span>
          <span data-id="element-1358" data-mp="1h7n8ly8al47y">Status</span>
          <span data-id="element-1359" data-mp="1h4raa8sj8diz">Next review</span>
          <span className="sr-only" data-id="element-1360" data-mp="1c80wjs479u0q">Actions</span>
        </div>
        {filtered.length === 0 ? <p className="px-4 py-12 text-center text-[15px] text-ink-2" data-id="element-1361" data-mp="178ehdd4ta3pa">
            {query ? `Nothing matches “${query}”.` : "No words here yet. Save some from the translator or the book."}
          </p> : <ul data-id="element-1362" data-mp="slpindidvujy">
            <AnimatePresence initial={false} data-id="element-1363" data-mp="sl7g5z54ok3l">
              {filtered.map(w => {
            const Source = sourceMeta[w.source].icon;
            return <motion.li key={w.id} layout initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0,
              transition: {
                duration: 0.12
              }
            }} transition={spring.stable} className="grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-1 border-b-2 border-line/10 px-4 py-3 last:border-b-0 md:grid-cols-[1.4fr_1.2fr_120px_130px_110px_48px] md:gap-4" data-id="element-1364" data-mp="jlsoiwd863hi">
                    <div className="flex min-w-0 items-center gap-3 md:col-auto" data-id="element-1365" data-mp="16koypwrtz14i">
                      <SpeakButton text={w.term} lang={course ? getLanguage(course.target).speech : "es-ES"} data-id="element-1366" data-mp="1ub2r5oebv1zm" />
                      <div className="min-w-0" data-id="element-1367" data-mp="1z32wxxh2wp44">
                        <p className="truncate text-[15px] font-bold text-ink" data-id="element-1368" data-mp="1z3vxggpg2idt">{w.term}</p>
                        <p className="truncate text-sm text-ink-2 md:hidden" data-id="element-1369" data-mp="3jqdefmd43kps">{w.translation}</p>
                      </div>
                    </div>
                    <p className="hidden truncate text-[15px] text-ink-2 md:block" data-id="element-1370" data-mp="3tij8d1mxcc7m">{w.translation}</p>
                    <span className="hidden items-center gap-1.5 text-sm text-ink-2 md:flex" data-id="element-1371" data-mp="3oiwtyovd0px8">
                      <Source className="h-4 w-4" aria-hidden="true" data-id="element-1372" data-mp="30ssriktwqw7t" /> {sourceMeta[w.source].label}
                    </span>
                    <span className="col-start-2 row-start-2 flex flex-wrap items-center gap-1.5 md:col-auto md:row-auto" data-id="element-1373" data-mp="2vw2bfxi3s0me">
                      <Tag tone={statusTone[w.status]} className="capitalize" data-id="element-1374" data-mp="3app2qyb6xwg2">
                        {w.status}
                      </Tag>
                      {w.usedIn > 0 && <span className="text-xs text-ink-2" data-id="element-1375" data-mp="q0huaaqv6590">in {w.usedIn} lessons</span>}
                    </span>
                    <span className="hidden text-sm text-ink-2 md:block" data-id="element-1376" data-mp="l1y5m2kvyfcy">{w.nextReview}</span>
                    <button type="button" onClick={() => remove(w.id)} aria-label={`Remove ${w.term}`} className="focus-ring col-start-3 row-span-2 row-start-1 justify-self-end rounded-lg p-2 text-ink-2 hover:bg-bad-tint hover:text-ink md:col-auto md:row-auto" data-id="element-1377" data-mp="2emhvsb2fxgna">
                      <Trash2Icon className="h-4 w-4" data-id="element-1378" data-mp="2y9pg0pi9lg9a" />
                    </button>
                  </motion.li>;
          })}
            </AnimatePresence>
          </ul>}
      </div>
    </div>;
}